
import React, { useEffect, useState } from 'react';
import { HashRouter, Routes, Route, Navigate, useNavigate, Outlet } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import VerificationPage from './pages/VerificationPage';
import AuthActionPage from './pages/AuthActionPage';
import StaffPage from './pages/StaffPage';
import ShiftPage from './pages/ShiftPage';
import ShiftCreationPage from './pages/ShiftCreationPage';
import SetupPage from './pages/SetupPage';
import SettingsPage from './pages/SettingsPage';
import UserShiftPage from './pages/UserShiftPage';
import UserDefaultSettingsPage from './pages/UserDefaultSettingsPage';
import SystemAdminPage from './pages/SystemAdminPage';
import InvitePage from './pages/InvitePage';
import NotificationToaster from './components/NotificationToaster';
import AdminAttendancePage from './pages/AdminAttendancePage';
import UserAttendancePage from './pages/UserAttendancePage';
import DebugMenu from './components/DebugMenu';
import RequireAuth from './components/RequireAuth';
import DashboardLayout from './components/DashboardLayout';
import UserLayout from './components/UserLayout';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { UserProvider } from './contexts/UserContext';
import { Loader2 } from 'lucide-react';

const TokenRedirectHandler: React.FC = () => {
    const navigate = useNavigate();
    const [isHandling, setIsHandling] = useState(true);
    
    useEffect(() => {
        const search = window.location.search;
        const params = new URLSearchParams(search);
        const token = params.get('token');
        const liffState = params.get('liff.state');
        
        // 招待URL（token付き）は招待画面へ。LINEのメニューなどから開いた場合（tokenなし）はログイン画面へ（LINEで自動ログイン）。
        const stateToken = liffState ? new URLSearchParams(liffState.split('?')[1] || '').get('token') : null;
        const currentHash = window.location.hash;
        if (token || stateToken) {
            if (!currentHash.includes('/invite')) navigate(`/invite?token=${encodeURIComponent((token || stateToken)!)}`, { replace: true });
        } else if (liffState && !currentHash.includes('/invite')) {
            navigate('/login', { replace: true });
        }
        setIsHandling(false);
    }, [navigate]);

    if (isHandling) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 fixed inset-0 z-50">
                <Loader2 className="animate-spin text-blue-600" size={40} />
            </div>
        );
    }
    return null;
};

// Layout Wrapper components for Outlet
const AdminLayoutWrapper = () => (
  <DashboardLayout>
    <Outlet />
  </DashboardLayout>
);

const UserLayoutWrapper = () => (
  <UserLayout>
    <Outlet />
  </UserLayout>
);

const App: React.FC = () => {
  return (
    <AuthProvider>
      <UserProvider>
      <ToastProvider>
        <HashRouter>
        <TokenRedirectHandler />
        <NotificationToaster />
        <div className="relative">
            <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/verification" element={<VerificationPage />} />
                <Route path="/auth/action" element={<AuthActionPage />} />
                <Route path="/invite" element={<InvitePage />} />
                
                <Route path="/system/dashboard" element={<RequireAuth><SystemAdminPage /></RequireAuth>} />
                
                {/* Admin Routes with Persistent Layout */}
                <Route path="/admin" element={<RequireAuth><AdminLayoutWrapper /></RequireAuth>}>
                  <Route path="shifts" element={<ShiftPage />} />
                  <Route path="shift-creation" element={<ShiftCreationPage />} />
                  <Route path="staff" element={<StaffPage />} />
                  <Route path="attendance" element={<AdminAttendancePage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route index element={<Navigate to="/admin/shifts" replace />} />
                </Route>

                <Route path="/admin/setup" element={<RequireAuth><SetupPage /></RequireAuth>} />

                {/* User Routes with Persistent Layout */}
                <Route path="/user" element={<RequireAuth><UserLayoutWrapper /></RequireAuth>}>
                  <Route path="shifts" element={<UserShiftPage />} />
                  <Route path="attendance" element={<UserAttendancePage />} />
                  <Route path="settings" element={<UserDefaultSettingsPage />} />
                  <Route index element={<Navigate to="/user/shifts" replace />} />
                </Route>
                
                <Route path="/" element={<Navigate to="/login" replace />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
            <DebugMenu />
        </div>
        </HashRouter>
      </ToastProvider>
      </UserProvider>
    </AuthProvider>
  );
};

export default App;
